import { PickingInfo, AbstractMesh, Vector2, Color3, Color4 } from '@babylonjs/core';
import { SlotElement } from './slotElement';
import store from '@/store';
import { ElementMenuItem } from './elementMenuItem';
import { SlotItem } from './slotItem';
import { util } from './util';
import { placementManager } from './placementManager';

export class SlotsInteractionManager {

    private selectedSlots: SlotElement[] = [];
    private lastPickedMesh: AbstractMesh | null = null;

    constructor(
        private scenePreview: any,
    ) { }

    public onMouseMove() {
        if (!store.getters.scene.selectedElementMenuItem) { return; }
        const pickInfo = this.pickInfo;
        const meshes: AbstractMesh[] = util.flatMap(
            this.scenePreview.slotElements,
            (x: SlotElement) => x.meshes,
        );
        if (pickInfo.pickedMesh === null) {
            this.lastPickedMesh = null;
            this.selectedSlots = [];
            meshes.forEach((mesh: AbstractMesh) =>
                mesh.disableEdgesRendering());
            return;
        }
        if (pickInfo.pickedMesh === this.lastPickedMesh ||
            !meshes.includes(pickInfo.pickedMesh)) { return; }
        this.lastPickedMesh = pickInfo.pickedMesh;
        this.selectedSlots = [];
        meshes.forEach((mesh: AbstractMesh) =>
            mesh.disableEdgesRendering());
        this.handleSlotSelection(pickInfo);
    }

    public onPointerUp() {
        this.onMouseMove();
        if (!this.selectedSlots.length ||
            store.getters.scene.tasks > 0) { return; }
        const pickInfo = this.pickInfo;
        if (pickInfo.pickedMesh === null) { return; }
        const meshes: AbstractMesh[] = util.flatMap(
            this.scenePreview.slotElements,
            (x: SlotElement) => x.meshes,
        );
        if (!meshes.includes(pickInfo.pickedMesh)) { return; }
        const slotElement = this.getPickedSlot(pickInfo, this.scenePreview.slotElements);
        const item: ElementMenuItem = store.getters.scene.selectedElementMenuItem;
        const position = this.getSlotPosition(slotElement, item);
        const slot = new SlotItem('', position, item.shape);
        store.commit('selectSlotItem', slot);
    }

    public clearHighlight() {
        const meshes: AbstractMesh[] = util.flatMap(
            this.scenePreview.slotElements,
            (x: SlotElement) => x.meshes,
        );
        meshes.forEach((mesh: AbstractMesh) =>
            mesh.disableEdgesRendering());
    }

    private handleSlotSelection(pickInfo: PickingInfo) {
        const item: ElementMenuItem = store.getters.scene.selectedElementMenuItem;
        const slots: SlotElement[] = [];
        const freeSlots = this.scenePreview.freeSlots;
        const baseSlot = this.getPickedSlot(pickInfo, freeSlots);
        if (!baseSlot) { return; }
        slots.push(baseSlot);
        const shapeY = item.shape.y ? item.shape.y : 1;
        this.fillSlots(item, slots, baseSlot, freeSlots, shapeY);
        const position = this.getSlotPosition(baseSlot, item);
        if (this.canPlace(item, slots, position, shapeY)) {
            this.selectedSlots = slots;
            this.highlightSlots(slots);
        }
    }

    private getPickedSlot(pickInfo: PickingInfo, freeSlots: SlotElement[]): SlotElement {
        return freeSlots.find((x: SlotElement) => x.meshes[0] === pickInfo.pickedMesh)!;
    }

    private getSlotPosition(baseSlot: SlotElement, item: ElementMenuItem): Vector2 {
        return new Vector2(
            baseSlot.position.x + (item.shape.x - 1) / 2,
            baseSlot.shape.y ?
                baseSlot.position.y - (item.shape.y - 1) / 2 :
                baseSlot.position.y,
        );
    }

    private canPlace(
        item: ElementMenuItem,
        slots: SlotElement[],
        position: Vector2,
        shapeY: number): boolean {
        const x = slots.length === item.shape.x * shapeY;
        const y = placementManager.canPlace(
            position,
            item.shape,
            item.type,
            item.specyficType,
        );
        return x && y;
    }

    private fillSlots(
        item: ElementMenuItem,
        slots: SlotElement[],
        baseSlot: SlotElement,
        freeSlots: SlotElement[],
        shapeY: number) {
        for (let i = 0; i < item.shape.x; i++) {
            for (let j = 0; j < shapeY; j++) {
                if (i === 0 && j === 0) { continue; }
                const posX = baseSlot.position.x + i;
                const posY = baseSlot.position.y - j;
                const slot = freeSlots
                    .find((x: SlotElement) =>
                        x.position.x === posX &&
                        x.position.y === posY,
                    );
                if (slot) {
                    slots.push(slot);
                }
            }
        }
    }

    private highlightSlots(slots: SlotElement[]) {
        slots.forEach((slot) => {
            slot.meshes.forEach((mesh) => {
                mesh.enableEdgesRendering();
                mesh.edgesColor = new Color4(1, 0, 0, 1);
                mesh.edgesWidth = 100;
            });
        });
    }

    private get pickInfo(): PickingInfo {
        return this.scenePreview.scene.pick(
            this.scenePreview.scene.pointerX,
            this.scenePreview.scene.pointerY,
        );
    }
}
